import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { db, auth } from './firebase';
import { logAdminAction } from './auditLog';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';

// Matches the backend's own staleness gate (render-backend/index.js,
// RIDER_STALE_MS) — isOnline alone can lag reality for a while after a
// rider disconnects, so every "online" display here should agree with
// what the dispatcher would actually use.
function isRiderReallyOnline(r) {
  return !!(r.isOnline && r.lastSeen && (Date.now() - new Date(r.lastSeen).getTime()) < 10 * 60 * 1000);
}

export default function PharmaciesRidersTab() {
  const [pharmacies, setPharmacies] = useState([]);
  const [riders, setRiders] = useState([]);
  const [activeView, setActiveView] = useState('pharmacies');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [docsModal, setDocsModal] = useState(null);

  useEffect(() => {
    const unsub1 = onValue(ref(db, 'pharmacies'), snap => {
      setPharmacies(snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })) : []);
      setLoading(false);
    });
    const unsub2 = onValue(ref(db, 'riders'), snap => {
      setRiders(snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })) : []);
    });
    return () => { unsub1(); unsub2(); };
  }, []);

  const editPcgLicense = async (pharmacy) => {
    const number = window.prompt('PCG License Number', pharmacy.pcgLicenseNumber || '');
    if (number === null) return;
    const expiry = window.prompt('PCG License Expiry (YYYY-MM-DD)', pharmacy.pcgLicenseExpiry || '');
    if (expiry === null) return;
    if (expiry && !/^\d{4}-\d{2}-\d{2}$/.test(expiry)) { alert('Expiry must be in YYYY-MM-DD format.'); return; }
    try {
      await update(ref(db, `pharmacies/${pharmacy.id}`), { pcgLicenseNumber: number.trim(), pcgLicenseExpiry: expiry });
      await logAdminAction('set_pcg_license', pharmacy.pharmacyName, { pharmacyId: pharmacy.id, pcgLicenseNumber: number.trim(), pcgLicenseExpiry: expiry });
    } catch (e) { alert('Could not save PCG license details.'); }
  };

  const togglePharmacyStatus = async (pharmacy) => {
    const newStatus = pharmacy.suspended ? false : true;
    setUpdating(pharmacy.id);
    try {
      await update(ref(db, `pharmacies/${pharmacy.id}`), {
        suspended: newStatus,
        suspendedAt: newStatus ? new Date().toISOString() : null,
      });
      if (newStatus) {
        await update(ref(db, `pharmacies_map/${pharmacy.id}`), { suspended: true, isOpen: false });
      } else {
        await update(ref(db, `pharmacies_map/${pharmacy.id}`), { suspended: false });
      }
      await logAdminAction(newStatus ? 'suspend_pharmacy' : 'reactivate_pharmacy', pharmacy.pharmacyName, { pharmacyId: pharmacy.id });
    } catch (e) { alert('Error updating pharmacy status'); }
    finally { setUpdating(null); }
  };

  const toggleRiderStatus = async (rider) => {
    const newStatus = rider.suspended ? false : true;
    setUpdating(rider.id);
    try {
      await update(ref(db, `riders/${rider.id}`), {
        suspended: newStatus,
        isApproved: !newStatus,
        suspendedAt: newStatus ? new Date().toISOString() : null,
      });
      await logAdminAction(newStatus ? 'suspend_rider' : 'reactivate_rider', rider.name, { riderId: rider.id });
    } catch (e) { alert('Error updating rider status'); }
    finally { setUpdating(null); }
  };

  const viewDocuments = async (rider) => {
    setDocsModal({ rider, loading: true, ghanaCard: null, selfie: null, error: '' });
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${BACKEND_URL}/admin/rider-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ riderId: rider.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load documents');
      setDocsModal({ rider, loading: false, ghanaCard: data.ghanaCard, selfie: data.selfie, error: '' });
    } catch (e) {
      setDocsModal({ rider, loading: false, ghanaCard: null, selfie: null, error: e.message || 'Could not load documents' });
    }
  };

  const approveRider = async (rider) => {
    setUpdating(rider.id);
    try {
      await update(ref(db, `riders/${rider.id}`), {
        isApproved: true,
        approvedAt: new Date().toISOString(),
      });
      await logAdminAction('approve_rider', rider.name, { riderId: rider.id });
    } catch (e) { alert('Error approving rider'); }
    finally { setUpdating(null); }
  };

  const filteredPharmacies = pharmacies.filter(p =>
    !search || p.pharmacyName?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase()) ||
    p.region?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredRiders = riders.filter(r =>
    !search || r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.email?.toLowerCase().includes(search.toLowerCase()) ||
    r.phone?.toLowerCase().includes(search.toLowerCase())
  );

  const STATUS_BADGE = (item) => {
    if (item.suspended) return { bg: '#FEE2E2', color: '#B91C1C', label: '🚫 Suspended' };
    if (item.status === 'approved' || item.isApproved) return { bg: '#DCFCE7', color: '#15803D', label: '✅ Active' };
    return { bg: '#FEF3C7', color: '#92400E', label: '⏳ Pending' };
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>🛡️ Pharmacies & Riders Management</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Superadmin only — Activate or suspend accounts for rule violations</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
            🔒 Superadmin Only
          </span>
          <input
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, width: 220, outline: 'none', backgroundColor: '#F7F8FA' }}
            placeholder={`Search ${activeView}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[
          { id: 'pharmacies', label: `💊 Pharmacies (${pharmacies.length})` },
          { id: 'riders', label: `🛵 Riders (${riders.length})` },
        ].map(t => (
          <button key={t.id} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeView === t.id ? 700 : 400, backgroundColor: activeView === t.id ? '#FFF' : 'transparent', color: activeView === t.id ? '#1D9E75' : '#888' }}
            onClick={() => { setActiveView(t.id); setSearch(''); }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* STATS */}
      {activeView === 'pharmacies' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Registered', value: pharmacies.length, color: '#1D9E75' },
            { label: 'Active', value: pharmacies.filter(p => p.status === 'approved' && !p.suspended).length, color: '#10B981' },
            { label: 'Pending Approval', value: pharmacies.filter(p => p.status === 'pending').length, color: '#F59E0B' },
            { label: 'Suspended', value: pharmacies.filter(p => p.suspended).length, color: '#EF4444' },
          ].map((st, i) => (
            <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB', textAlign: 'center' }}>
              <p style={{ fontSize: 26, fontWeight: 800, color: st.color, margin: '0 0 4px' }}>{st.value}</p>
              <p style={{ fontSize: 12, color: '#888', margin: 0 }}>{st.label}</p>
            </div>
          ))}
        </div>
      )}

      {activeView === 'riders' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Riders', value: riders.length, color: '#1D9E75' },
            { label: 'Active', value: riders.filter(r => r.isApproved && !r.suspended).length, color: '#10B981' },
            { label: 'Online Now', value: riders.filter(r => isRiderReallyOnline(r) && !r.suspended).length, color: '#3B82F6' },
            { label: 'Suspended', value: riders.filter(r => r.suspended).length, color: '#EF4444' },
          ].map((st, i) => (
            <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB', textAlign: 'center' }}>
              <p style={{ fontSize: 26, fontWeight: 800, color: st.color, margin: '0 0 4px' }}>{st.value}</p>
              <p style={{ fontSize: 12, color: '#888', margin: 0 }}>{st.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* PHARMACIES TABLE */}
      {activeView === 'pharmacies' && (
        loading ? <p style={{ color: '#888', padding: 20 }}>Loading...</p> :
        filteredPharmacies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 50, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
            <p style={{ fontSize: 36 }}>💊</p>
            <p style={{ fontWeight: 600, color: '#888' }}>No pharmacies found</p>
          </div>
        ) : (
          <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#F7F8FA' }}>
                  {['Pharmacy', 'Owner', 'Phone', 'Region', 'License', 'PCG License', 'Status', 'Joined', 'Action'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPharmacies.map((p, i) => {
                  const badge = STATUS_BADGE(p);
                  return (
                    <tr key={p.id} style={{ backgroundColor: p.suspended ? '#FFF5F5' : i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <p style={{ fontWeight: 700, margin: '0 0 2px', color: '#1A1A1A' }}>{p.pharmacyName || '—'}</p>
                        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{p.email}</p>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', color: '#555' }}>{p.ownerName || '—'}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', color: '#555' }}>{p.phone || '—'}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', color: '#555' }}>{p.region || '—'}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <span style={{ fontSize: 11, backgroundColor: '#E8F8F2', color: '#1D9E75', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                          {p.licenseNumber || 'Not set'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        {(() => {
                          const expired = p.pcgLicenseExpiry && p.pcgLicenseExpiry < new Date().toISOString().slice(0, 10);
                          return (
                            <button
                              style={{ fontSize: 11, backgroundColor: !p.pcgLicenseNumber ? '#FEF3C7' : expired ? '#FEE2E2' : '#E8F8F2', color: !p.pcgLicenseNumber ? '#92400E' : expired ? '#B91C1C' : '#1D9E75', padding: '3px 8px', borderRadius: 20, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                              onClick={() => editPcgLicense(p)}
                              title="Click to edit"
                            >
                              {!p.pcgLicenseNumber ? '⏳ Not set' : expired ? `❌ Expired ${p.pcgLicenseExpiry}` : `✅ ${p.pcgLicenseNumber} · ${p.pcgLicenseExpiry || 'no expiry'}`}
                            </button>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <span style={{ backgroundColor: badge.bg, color: badge.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <button
                          style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: updating === p.id ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: p.suspended ? '#DCFCE7' : '#FEE2E2', color: p.suspended ? '#15803D' : '#B91C1C', opacity: updating === p.id ? 0.6 : 1 }}
                          onClick={() => {
                            if (window.confirm(p.suspended ? `Reactivate ${p.pharmacyName}? They will appear on the customer map again.` : `Suspend ${p.pharmacyName}? They will be hidden from the customer map and cannot receive orders.`)) {
                              togglePharmacyStatus(p);
                            }
                          }}
                          disabled={updating === p.id}
                        >
                          {updating === p.id ? '...' : p.suspended ? '✅ Reactivate' : '🚫 Suspend'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* RIDERS TABLE */}
      {activeView === 'riders' && (
        filteredRiders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 50, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
            <p style={{ fontSize: 36 }}>🛵</p>
            <p style={{ fontWeight: 600, color: '#888' }}>No riders found</p>
          </div>
        ) : (
          <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#F7F8FA' }}>
                  {['Rider', 'Phone', 'Deliveries', 'Earnings', 'Status', 'Online', 'Joined', 'Action'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRiders.map((r, i) => {
                  const badge = STATUS_BADGE(r);
                  return (
                    <tr key={r.id} style={{ backgroundColor: r.suspended ? '#FFF5F5' : i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <p style={{ fontWeight: 700, margin: '0 0 2px', color: '#1A1A1A' }}>{r.name || '—'}</p>
                        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{r.email}</p>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', color: '#555' }}>{r.phone || '—'}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 700, color: '#1D9E75' }}>{r.totalDeliveries || 0}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 700, color: '#1D9E75' }}>GH₵ {(r.totalEarnings || 0).toFixed(2)}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <span style={{ backgroundColor: badge.bg, color: badge.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{badge.label}</span>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        <span style={{ backgroundColor: isRiderReallyOnline(r) ? '#DCFCE7' : '#F3F4F6', color: isRiderReallyOnline(r) ? '#15803D' : '#888', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          {isRiderReallyOnline(r) ? '🟢 Online' : '⚫ Offline'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 6 }}>
                        <button
                          style={{ padding: '6px 14px', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#FFF', color: '#374151' }}
                          onClick={() => viewDocuments(r)}
                        >
                          🪪 View ID
                        </button>
                        {!r.suspended && !r.isApproved && (
                          <button
                            style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: updating === r.id ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#DCFCE7', color: '#15803D', opacity: updating === r.id ? 0.6 : 1 }}
                            onClick={() => {
                              if (window.confirm(`Approve ${r.name}? They will be able to accept and deliver orders.`)) {
                                approveRider(r);
                              }
                            }}
                            disabled={updating === r.id}
                          >
                            {updating === r.id ? '...' : '✅ Approve'}
                          </button>
                        )}
                        <button
                          style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: updating === r.id ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: r.suspended ? '#DCFCE7' : '#FEE2E2', color: r.suspended ? '#15803D' : '#B91C1C', opacity: updating === r.id ? 0.6 : 1 }}
                          onClick={() => {
                            if (window.confirm(r.suspended ? `Reactivate ${r.name}? They can start accepting deliveries again.` : `Suspend ${r.name}? They will not be able to accept any orders.`)) {
                              toggleRiderStatus(r);
                            }
                          }}
                          disabled={updating === r.id}
                        >
                          {updating === r.id ? '...' : r.suspended ? '✅ Reactivate' : '🚫 Suspend'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {docsModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
             onClick={() => setDocsModal(null)}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 14, padding: 24, width: 560, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, margin: 0, fontSize: 16 }}>🪪 {docsModal.rider.name || 'Rider'} — Verification Documents</h3>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }} onClick={() => setDocsModal(null)}>✕</button>
            </div>

            {docsModal.loading ? (
              <p style={{ color: '#888', textAlign: 'center', padding: 30 }}>Loading…</p>
            ) : docsModal.error ? (
              <p style={{ color: '#EF4444', textAlign: 'center', padding: 30 }}>{docsModal.error}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>GHANA CARD</p>
                  {docsModal.ghanaCard
                    ? <img src={docsModal.ghanaCard} alt="Ghana Card" style={{ width: '100%', borderRadius: 10, border: '1px solid #E5E7EB' }} />
                    : <p style={{ color: '#B91C1C', fontSize: 13 }}>Not uploaded.</p>}
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>SELFIE</p>
                  {docsModal.selfie
                    ? <img src={docsModal.selfie} alt="Selfie" style={{ width: '100%', maxWidth: 260, borderRadius: 10, border: '1px solid #E5E7EB' }} />
                    : <p style={{ color: '#B91C1C', fontSize: 13 }}>Not uploaded.</p>}
                </div>
                <p style={{ fontSize: 11, color: '#AAA', margin: 0 }}>This link expires in 5 minutes — reopen this dialog to refresh it.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}